'use client'

/**
 * FigureActions — the interactive toolbar that turns a static, server-rendered
 * data figure into a downloadable, shareable Fridge.com asset (owner directive
 * 07/20, Section E items 14-15).
 *
 * Rendered inside a <figure> (it walks up to the nearest ancestor <figure> at
 * click time and serializes the <svg> within), so no id plumbing is needed and
 * it can never target the wrong graphic. Two actions:
 *
 *  • Download PNG — rasterizes the figure's inline SVG (which already carries an
 *    embedded Fridge.com wordmark) onto a 2× white canvas and saves it as
 *    `fridge-com-<slug>.png`. Front-and-back branding: the wordmark is baked
 *    into the pixels, the Fridge.com name is in the filename.
 *  • Share — copies a deep link to this exact figure's section (origin + path +
 *    #anchor) and can open X / LinkedIn intents. Every section becomes
 *    individually shareable.
 *
 * Light styling only, brand palette, and it is the only client JS on the /data
 * figures — the underlying SVG stays server-rendered for SEO and AI crawlers.
 */
import { useRef, useState } from 'react'
import { Download } from 'lucide-react'
import { ShareBar } from '@/components/data/share-bar'

/**
 * Swap every `data-fridge-wordmark` <image> href in the clone for the real
 * base64 wordmark, so the rasterized export carries the logo pixels the page
 * itself references by URL. Returns how many were inlined. Exported for the
 * guard test; the data-URI module loads lazily so it never ships with the page.
 */
export async function inlineWordmarksForExport(clone: SVGSVGElement): Promise<number> {
  const marks = clone.querySelectorAll<SVGImageElement>('image[data-fridge-wordmark]')
  if (!marks.length) return 0
  const { FRIDGE_LOGO_BLUE_DATA_URI } = await import('@/lib/brand-logo-data-uri')
  marks.forEach((el) => el.setAttribute('href', FRIDGE_LOGO_BLUE_DATA_URI))
  return marks.length
}

interface FigureActionsProps {
  /** Download filename stem — the component prefixes it as `fridge-com-<slug>.png`. */
  slug: string
  /** Human title used in share copy and the PNG's accessible name. */
  shareTitle: string
  /** Section anchor id this figure lives under, for the shareable deep link. */
  anchorId?: string
}

function toast(msg: string) {
  if (typeof window === 'undefined') return
  const el = document.createElement('div')
  el.textContent = msg
  el.className =
    'fixed bottom-4 right-4 bg-gray-900 text-white text-[13px] font-bold px-4 py-2 rounded-full shadow-lg z-50'
  document.body.appendChild(el)
  setTimeout(() => {
    if (document.body.contains(el)) document.body.removeChild(el)
  }, 2000)
}

export function FigureActions({ slug, shareTitle, anchorId }: FigureActionsProps) {
  const anchorRef = useRef<HTMLDivElement>(null)
  const [busy, setBusy] = useState(false)

  const fileName = `fridge-com-${slug}`

  const downloadPng = async () => {
    // Pick the FIGURE's chart SVG, not a toolbar icon: this same toolbar renders
    // small lucide/share <svg>s that precede the chart in document order, so a
    // naive querySelector('svg') would export a 14px icon. Choose the largest
    // rendered SVG in the figure — the chart dwarfs every icon.
    const fig = anchorRef.current?.closest('figure')
    const svg = fig
      ? [...fig.querySelectorAll('svg')]
          .map((el) => ({ el, r: el.getBoundingClientRect() }))
          .sort((a, b) => b.r.width * b.r.height - a.r.width * a.r.height)[0]?.el
      : null
    if (!svg) {
      toast('Nothing to export')
      return
    }
    setBusy(true)
    try {
      // Intrinsic size from the viewBox; fall back to the rendered box.
      const vb = svg.getAttribute('viewBox')?.split(/\s+/).map(Number)
      const w = vb && vb.length === 4 ? vb[2] : svg.clientWidth || 720
      const h = vb && vb.length === 4 ? vb[3] : svg.clientHeight || 320

      const clone = svg.cloneNode(true) as SVGSVGElement
      clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg')
      clone.setAttribute('width', String(w))
      clone.setAttribute('height', String(h))

      // The served figure references the wordmark by same-origin URL so ~53 KB
      // of base64 never ships with the page (FRIDGE-179 class — eight copies
      // blew /data/price-index's byte budget on 08/23/2026). But SVG-as-image
      // rasterization loads NO external resources, so serializing that href
      // as-is would silently drop the wordmark from the exported pixels — and
      // the owner's 07/20 directive is that the REAL logo bakes into exports.
      // Inline the exact bytes into the CLONE only, at click time; the 53 KB
      // module is dynamic-imported here and never joins the page bundle.
      const inlined = await inlineWordmarksForExport(clone)

      // FRIDGE-591 (owner 08/27: "100% of data needs the Fridge.com logo
      // within it"): the recharts-drawn interactive figures carry NO
      // data-fridge-wordmark inside their SVG — only the static server
      // figures do — so their exports left Fridge.com behind. When the clone
      // has no wordmark, bake one into the bottom-right corner of the export.
      if (inlined === 0) {
        const { FRIDGE_LOGO_BLUE_DATA_URI } = await import('@/lib/brand-logo-data-uri')
        const markW = Math.max(90, Math.round(w * 0.14))
        const markH = Math.round(markW * 0.21)
        const pad = Math.round(Math.min(w, h) * 0.02) + 6
        const mark = document.createElementNS('http://www.w3.org/2000/svg', 'image')
        mark.setAttribute('href', FRIDGE_LOGO_BLUE_DATA_URI)
        mark.setAttribute('x', String(w - markW - pad))
        mark.setAttribute('y', String(h - markH - pad))
        mark.setAttribute('width', String(markW))
        mark.setAttribute('height', String(markH))
        mark.setAttribute('preserveAspectRatio', 'xMaxYMax meet')
        mark.setAttribute('opacity', '0.92')
        mark.setAttribute('data-fridge-wordmark', 'export-injected')
        clone.appendChild(mark)
      }

      const xml = new XMLSerializer().serializeToString(clone)
      const svgBlob = new Blob(
        ['<?xml version="1.0" encoding="UTF-8"?>\n', xml],
        { type: 'image/svg+xml;charset=utf-8' }
      )
      const url = URL.createObjectURL(svgBlob)

      const scale = 2
      const img = new Image()
      img.decoding = 'async'
      await new Promise<void>((resolve, reject) => {
        img.onload = () => resolve()
        img.onerror = () => reject(new Error('render failed'))
        img.src = url
      })

      const canvas = document.createElement('canvas')
      canvas.width = Math.round(w * scale)
      canvas.height = Math.round(h * scale)
      const ctx = canvas.getContext('2d')
      if (!ctx) throw new Error('no 2d context')
      ctx.fillStyle = '#ffffff'
      ctx.fillRect(0, 0, canvas.width, canvas.height)
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
      URL.revokeObjectURL(url)

      await new Promise<void>((resolve) => {
        canvas.toBlob((blob) => {
          if (!blob) {
            toast('Export failed')
            resolve()
            return
          }
          const a = document.createElement('a')
          a.href = URL.createObjectURL(blob)
          a.download = `${fileName}.png`
          document.body.appendChild(a)
          a.click()
          document.body.removeChild(a)
          setTimeout(() => URL.revokeObjectURL(a.href), 1000)
          resolve()
        }, 'image/png')
      })
      toast('Saved PNG')
    } catch {
      toast('Export failed')
    } finally {
      setBusy(false)
    }
  }

  const btn =
    'inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full border border-gray-200 bg-white text-[11px] font-bold text-gray-600 hover:border-[#ff9951] hover:text-gray-900 transition-colors disabled:opacity-50 disabled:cursor-wait'

  return (
    // The toolbar itself may wrap (PNG button on one line, the share row on
    // the next) so the ShareBar's one-line icon row always fits fully — zero
    // clipping at any width (owner spec 07/20). Left-aligned when the figure
    // header is stacked on mobile; right-aligned inline from sm up.
    <div ref={anchorRef} className="flex flex-wrap items-center gap-x-1.5 gap-y-2 justify-start sm:justify-end min-w-0">
      <button type="button" onClick={downloadPng} disabled={busy} className={`${btn} text-[#0469f0]`} aria-label={`Download ${shareTitle} as a Fridge.com PNG`}>
        <Download className="w-3.5 h-3.5" />
        PNG
      </button>
      {/* Per-figure all-network share (owner: per-figure AND per-page). */}
      <ShareBar variant="compact" title={shareTitle} anchorId={anchorId} utmContent={`figure-${slug}`} />
    </div>
  )
}
